/**
 * CancelEltJobExecutionModal Component
 *
 * Modal for confirming job execution cancellation.
 */

import React, { useState } from "react";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { Alert } from "../../../components/ui/Alert";
import { useCancelEltJobExecution } from "../hooks/useCancelEltJobExecution";
import type { EltJobExecution } from "../../../types/elt-job-execution.types";

interface CancelEltJobExecutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  execution: EltJobExecution;
}

/**
 * CancelEltJobExecutionModal component
 */
export const CancelEltJobExecutionModal: React.FC<CancelEltJobExecutionModalProps> = ({ isOpen, onClose, tenantId, execution }) => {
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { mutate: cancelExecution, isPending } = useCancelEltJobExecution(tenantId, {
    onSuccess: () => {
      onClose();
    },
    onError: (error) => {
      setErrorMessage(error);
    },
  });

  const handleCancel = () => {
    setErrorMessage("");
    cancelExecution(execution.jobExecutionId);
  };

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage("");
      onClose();
    }
  };

  const isInProgress = execution.status === "in_progress";

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>Cancel Execution</Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Warning for in-progress executions */}
          {isInProgress && (
            <Alert variant="warning">This execution is currently running. The agent will stop processing after the current query completes.</Alert>
          )}

          {/* Confirmation Message */}
          <p className="text-sm text-gray-600">
            Are you sure you want to cancel the execution <span className="font-medium text-gray-900">{execution.jobExecutionCode}</span>?
          </p>

          {execution.status === "pending" && (
            <p className="text-sm text-gray-500">This execution has not started yet and will be cancelled immediately.</p>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Keep Running
          </Button>
          <Button variant="danger" onClick={handleCancel} loading={isPending}>
            Cancel Execution
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
